import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useForm } from "react-hook-form";
import { useCallback, useEffect } from "react";
import { MotivoDemissao } from "../../../../domain/models/motivo_demissao";
import { defaultMotivoDemissaoForm, MotivoDemissaoForm, MotivoDemissaoSchema } from "../../../schemas/MotivoDemissao_lista/motivo_demissao";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormSwitchInput } from "../../../../../../common/components/masked_inputs/react_forms/form_switch_input";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { ModalContainer } from "../../../../../../common/components/modal_container";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";

import DefaultFormInput from "../../../../../../common/components/default_form_input";
import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";

/**
 * CadastroMotivoDemissaoModal
 */

type Props = {
  open: boolean,
  saving: boolean,
  selectedMotivoDemissao: MotivoDemissao | null,
  onSubmit: (form: MotivoDemissaoForm) => void,
  onOpenChangeHistory: () => void,
  onClose: () => void,
};

export const CadastroMotivoDemissaoModal = ({
  open,
  saving,
  selectedMotivoDemissao,
  onSubmit,
  onOpenChangeHistory,
  onClose,
}: Props) => {
  const hookForm = useForm<MotivoDemissaoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: defaultMotivoDemissaoForm,
    resolver: zodResolver(MotivoDemissaoSchema),
  });

  useEffect(() => {
    if (open) {
      if (selectedMotivoDemissao) {
        hookForm.setValue('ativo', selectedMotivoDemissao.ativo ? 1 : 0);
        hookForm.setValue('descricaoMotivo', selectedMotivoDemissao.descricaoMotivo ?? '');
      } else {
        hookForm.reset();
      }
    }
  }, [
    hookForm,
    open,
    selectedMotivoDemissao,
  ]);

  const handleSubmit = useCallback((form: MotivoDemissaoForm) => {
    if (!saving) onSubmit(form);
  }, [saving, onSubmit]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [saving, onClose]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "45rem",
          },
        },
      }}>

      <DialogTitle>
        Cadastrar Motivo Demissão
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <ModalContainer>
            <FormContainer title={"Informações"} arrangement="fullRow" border={false}>
              <>
                <FormSwitchInput
                  name='ativo'
                  readOnly={saving}
                  reactHookForm={hookForm} />

                <DefaultFormInput
                  name='descricaoMotivo'
                  label='Motivo Demissão'
                  readOnly={saving}
                  required={true}
                  multiline={true}
                  rows={3}
                  reactHookForm={hookForm}
                />

              </>
            </FormContainer>
          </ModalContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex px-4 pb-4 gap-4">
          <div>
            {
              isNotNullOrUndefined(selectedMotivoDemissao) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={onOpenChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>

            }
          </div>

          <div className="flex flex-row">
            <div className="flex flex-row gap-2">
              <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                <h1
                  className="text-sm text-black dark:text-white"
                  onClick={handleClose}>
                  Cancelar
                </h1>
              </div>

              <div className="w-[120px]">
                {saving ? (
                  <PrimaryButton
                    buttonColor="primary-green">
                    Salvando...
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    type="submit"
                    buttonColor="primary-green">
                    Salvar
                  </PrimaryButton>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}